import React from "react";

import Col from "antd/es/col";
import Row from "antd/es/row";

import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import User from "@mapmycustomers/shared/types/User";

import ChartCard from "@app/scene/reports/components/charts/ChartCard";
import NoChart from "@app/scene/reports/components/charts/NoChart";
import DealsRottingCard from "@app/scene/reports/components/Deals/components/Charts/DealsRotting";
import FunnelStatsData from "@app/scene/reports/store/FunnelStatsData";
import layout from "@app/styles/layout";

import styles from "./Charts.module.scss";
import DealsPerStageChart from "./DealsPerStageChart";
import DealsProgressionChart from "./ProgressionChart";
import WonLossPieChart from "./WonLossPieChart";

interface Props {
  dealsLost: number;
  dealsWon: number;
  funnelsStatsData: FunnelStatsData;
  grossLostRevenue: number;
  grossWonRevenue: number;
  selectedFunnel?: Funnel;
  userIds: User["id"][];
}

const Charts: React.FC<Props> = ({
  dealsLost,
  dealsWon,
  funnelsStatsData,
  grossLostRevenue,
  grossWonRevenue,
  selectedFunnel,
  userIds,
}) => {
  const pieDataDealsPerStage = {
    chart: {
      height: 350,
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: "pie",
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
        },
        showInLegend: true,
      },
    },
    series: [
      {
        colorByPoint: true,
        data: funnelsStatsData.stageSummary.map((item) => ({
          name: item.stage.name,
          y: item.totalDeals,
        })),
        name: "# Deals",
      },
    ],
    title: {
      text: "Deals Per Stage",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.y}</b>",
    },
  };

  // ============== num closed deals ============== //
  const pieDataNumClosedDeals = {
    title: {
      text: "Deals Won & Lost",
    },
  };

  // ============== amount closed deals ============== //
  const pieDataRevenueClosedDeals = {
    title: {
      text: "Revenue Won vs. Lost",
    },
  };

  return (
    <div className={styles.container}>
      <Row gutter={[layout.spacerXL, layout.spacerXL]}>
        <Col sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 8 }}>
          <ChartCard className={styles.pieChart}>
            {dealsWon > 0 || dealsLost > 0 ? (
              <WonLossPieChart
                chartTitle={pieDataNumClosedDeals.title.text}
                funnel={selectedFunnel}
                lost={dealsLost}
                userIds={userIds}
                whichConfigToUse="dealsWonLoss"
                won={dealsWon}
              />
            ) : (
              <NoChart chartTitle={pieDataNumClosedDeals.title.text} />
            )}
          </ChartCard>
        </Col>
        <Col sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 8 }}>
          <ChartCard className={styles.pieChart}>
            {grossLostRevenue > 0 ? (
              <WonLossPieChart
                chartTitle={pieDataRevenueClosedDeals.title.text}
                funnel={selectedFunnel}
                lost={Number(grossLostRevenue)}
                userIds={userIds}
                whichConfigToUse="revenueWonLoss"
                won={Number(grossWonRevenue)}
              />
            ) : (
              <NoChart chartTitle={pieDataRevenueClosedDeals.title.text} />
            )}
          </ChartCard>
        </Col>
        <Col sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 8 }}>
          <ChartCard className={styles.pieChart}>
            {funnelsStatsData.stageSummary.length > 0 ? (
              <DealsPerStageChart
                chartTitle={pieDataDealsPerStage.title.text}
                funnel={selectedFunnel}
                userIds={userIds}
              />
            ) : (
              <NoChart chartTitle={pieDataDealsPerStage.title.text} />
            )}
          </ChartCard>
        </Col>

        <Col span={24}>
          <DealsProgressionChart funnel={selectedFunnel} userIds={userIds} />
        </Col>
        <Col span={12}>
          <DealsRottingCard />
        </Col>
      </Row>
    </div>
  );
};

export default Charts;
